import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'

import Close from '../../assets/close.svg'
import Icon from '../Icon'
import Search from '../Form/Search'

import i18n from '../../i18n'
import Logo from '../../assets/thuisarts-logo.svg'
import breakpoints from '../../constants/breakpoints'

export type AutoSuggestion = { title: string; url: string }

interface State {
  autoSuggestions: AutoSuggestion[]
  loading: boolean
  showSuggestions: boolean
  value: string
}

const TopHeader: React.FC = () => {
  const [state, setState] = useState<State>({
    autoSuggestions: [],
    loading: false,
    showSuggestions: false,
    value: '',
  })
  const { autoSuggestions, showSuggestions, value } = state
  const headerRef = useRef<HTMLDivElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (suggestionsRef.current) {
      const links = suggestionsRef.current.querySelectorAll('a')
      const handleLinkKeyDown = (event: any, index: number) => {
        if (event.keyCode === 38 && index > 0) {
          return links[index - 1].focus()
        }

        if (event.keyCode === 38 && index === 0 && headerRef.current) {
          const search = headerRef.current.querySelector('input')

          if (search) {
            return search.focus()
          }

          return null
        }

        if (event.keyCode === 40 && index < links.length - 1) {
          return links[index + 1].focus()
        }

        if (event.keyCode === 9 && index === links.length - 1 && headerRef.current) {
          const search = headerRef.current.querySelector('input')

          if (search) {
            event.preventDefault()
            return search.focus()
          }

          return null
        }

        if (event.keyCode === 27) {
          setState({ ...state, autoSuggestions: [], showSuggestions: false })
        }

        return null
      }

      links.forEach((link, index) => {
        link.setAttribute('tabIndex', '0')
        link.addEventListener('keydown', (event) => handleLinkKeyDown(event, index))
      })
    }
  }, [autoSuggestions])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setState((s) => ({ ...s, showSuggestions: false }))
        return
      }
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const fetchAutoSuggestions = async () => {
    const { data } = await axios.get(`https://${window.location.hostname}/ta-search-suggestions`)
    return data as AutoSuggestion[]
  }

  const compareByStartsWith = (query: string) => (a: AutoSuggestion, b: AutoSuggestion) => {
    const aStartsWithValue = a.title.toLowerCase().startsWith(query)
    const bStartsWithValue = b.title.toLowerCase().startsWith(query)

    if (aStartsWithValue === bStartsWithValue) {
      return 0
    }
    if (aStartsWithValue && !bStartsWithValue) {
      return -1
    }
    return 1
  }

  const setAutoSuggestions = async (value: string) => {
    if (!value?.length) {
      setState({ ...state, autoSuggestions: [], value, showSuggestions: false })
      return
    }

    try {
      setState({ ...state, loading: true })

      const fetchedSuggestions = (await fetchAutoSuggestions()) || []

      const filteredAndSortedSuggestions = fetchedSuggestions
        .filter((s) => s.title.toLowerCase().includes(value.toLowerCase()))
        .sort(compareByStartsWith(value))

      return setState({
        ...state,
        autoSuggestions: filteredAndSortedSuggestions,
        loading: false,
        value: value,
        showSuggestions: true,
      })
    } catch (error) {
      return console.error(error)
    }
  }

  const onSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.keyCode === 40 && suggestionsRef.current && autoSuggestions.length > 0) {
      const firstLink = suggestionsRef.current.querySelector('a')

      if (firstLink) {
        return firstLink.focus()
      }

      return null
    }

    return null
  }

  const submitSearch = (value: string) => (window.location.href = `/zoeken?s=${value}`)

  const setStateSearchActive = (val: boolean) => () =>
    setState((s) => ({ ...s, showSuggestions: val }))

  return (
    <Container id="top-header" ref={headerRef}>
      <Content>
        <LogoWrapper $searchIsActive={showSuggestions}>
          <a href="https://www.thuisarts.nl">
            <img src={Logo} alt="Home Thuisarts.nl" />
          </a>
        </LogoWrapper>
        <SearchWrapper $isActive={showSuggestions}>
          <Search
            buttonText={i18n.t('topHeader.search')}
            label={i18n.t('topHeader.label')}
            placeholder={i18n.t('topHeader.searchPlaceholder')}
            handleOnChange={setAutoSuggestions}
            handleOnKeyDown={onSearchKeyDown}
            handleOnSubmit={submitSearch}
            handleToggleFocus={setStateSearchActive(true)}
          />
        </SearchWrapper>
        <SearchToggle $isActive={Boolean(value?.length)} onClick={setStateSearchActive(false)}>
          <Icon icon={Close} />
          <SearchToggleText>{i18n.t('topHeader.cancel')}</SearchToggleText>
        </SearchToggle>
      </Content>
      <Suggestions $isVisible={Boolean(showSuggestions && autoSuggestions?.length)}>
        <SuggestionsContent ref={suggestionsRef}>
          {autoSuggestions.map((suggestion) => {
            const highlightedSuggestion = String(suggestion.title).replace(
              new RegExp(value, 'i'),
              '<strong>$&</strong>',
            )
            const markup = () => ({
              __html: `<a href='${suggestion.url}'>${highlightedSuggestion}</a>`,
            })

            return <SuggestionLink key={suggestion.title} dangerouslySetInnerHTML={markup()} />
          })}
        </SuggestionsContent>
      </Suggestions>
    </Container>
  )
}

const Container = styled.div`
  display: block;
  height: var(--headerHeight);
  padding-left: 15px;
  border-bottom: 1px solid #f3f3f3;
  background: white;
  opacity: 1;
  transition: transform 0.2s linear;
  z-index: 999;

  @media (min-width: ${breakpoints.sm}) {
    padding-left: 20px;
  }

  @media (min-width: ${breakpoints.md}) {
    padding-left: 30px;
  }

  &.scrolled {
    transform: translateY(-100%);
  }
`

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--headerHeight);
`

const LogoWrapper = styled.div<{ $searchIsActive: boolean }>`
  line-height: initial;
  margin-top: 2px;

  img {
    transition:
      opacity 0.3s,
      margin 0.3s,
      width 0.3s;
    height: 48px;

    @media (max-width: ${breakpoints.md}) {
      width: 50px;
      object-fit: cover;
      object-position: right;
    }
  }
`

const SearchWrapper = styled.div<{ $isActive: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  transition:
    opacity 0.3s,
    transform 0.3s;
  margin-right: 240px;

  & input {
    border: 1px solid #949494;
    padding: 0.563rem 3.313rem;
    border-radius: 4px;
    width: 100%;
  }

  @media (max-width: 1119px) {
    margin-left: 0;
  }

  @media (max-width: ${breakpoints.lg}) {
    margin-right: 80px;
  }

  @media (max-width: ${breakpoints.sm}) {
    margin-right: 48px;
  }
`

const SearchToggle = styled.button<{ $isActive: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: absolute;
  right: 20px;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0 0;
  color: ${({ theme }) => theme.warmGrey};
  line-height: 33px;
  font-size: 15px;
  font-family: 'Circular', sans-serif;
  border: none;
  transition:
    opacity 0.3s,
    color 0.3s;
  background: transparent;
  outline: none;

  &:active,
  &:focus,
  &:focus-visible {
    outline: none;
  }

  svg {
    width: 12px;
    height: 12px;

    g {
      transition: fill 0.3s;
      fill: ${({ theme }) => theme.warmGrey};
    }
  }

  &:hover {
    color: ${({ theme }) => theme.green};

    svg {
      g {
        fill: ${({ theme }) => theme.green};
      }
    }
  }

  ${({ $isActive }) =>
    !$isActive &&
    `
      visibility: hidden;
      opacity: 0;
      width: 0;
  `}
`

const SearchToggleText = styled.div`
  @media (max-width: ${breakpoints.xl}) {
    display: none;
  }
`

const Suggestions = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: var(--headerHeight);
  left: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  height: calc((var(--vh) * 100) - var(--headerHeight));
  background-color: hsla(0, 0%, 100%, 0.95);
  transition: opacity 300ms;

  ${({ $isVisible }) =>
    !$isVisible &&
    `
      visibility: hidden;
      opacity: 0;
  `}
`

const SuggestionsContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 600px;
  margin: 0 24px;

  @media (max-width: 1120px) {
    width: 100%;
  }
`

const SuggestionLink = styled.span`
  margin-top: 16px;

  :first-child {
    margin-top: 30px;
  }

  a {
    cursor: pointer;
    color: ${({ theme }) => theme.textColor};
    line-height: 18px;
    font-family: 'Circular', sans-serif;
    font-size: 18px;
    text-decoration: none;
    padding: 5px 0;
  }

  a:visited {
    color: ${({ theme }) => theme.linkVisitedColor};
  }

  a:hover {
    color: ${({ theme }) => theme.blue};
  }

  a:focus {
    color: ${({ theme }) => theme.blue};
  }
`

export default TopHeader
